import { Button, Card, Col, Row, Select, Space } from "antd";
import type { DataLockOptionField } from "../../services/domains/data-lock/types";
import styles from "./DataLock.module.scss";
import { OrderFieldValueSelect } from "./OrderFieldValueSelect";
import type { DataLockAttributeFormRow, DataLockFormOption } from "./types";

export type OrderScopeEditorValue = {
  restrictedAttributes: DataLockAttributeFormRow[];
  whitelistedAttributes: DataLockAttributeFormRow[];
};

export type OrderScopeEditorProps = OrderScopeEditorValue & {
  fields: DataLockOptionField[];
  allOption: DataLockFormOption;
  disabled?: boolean;
  onChange: (next: OrderScopeEditorValue) => void;
};

type ScopeKey = keyof OrderScopeEditorValue;

type FieldOption = {
  value: string;
  label: string;
};

let generatedRowKeySequence = 0;

const normalizeFields = (fields: unknown): FieldOption[] => {
  if (!Array.isArray(fields)) return [];

  const seen = new Set<string>();
  return fields.reduce<FieldOption[]>((options, item) => {
    if (!item || typeof item !== "object") return options;

    const field = item as Partial<DataLockOptionField>;
    if (typeof field.code !== "string" || !field.code.trim()) return options;
    if (seen.has(field.code)) return options;

    seen.add(field.code);
    options.push({
      value: field.code,
      label:
        typeof field.name === "string" && field.name.trim()
          ? field.name
          : field.code,
    });
    return options;
  }, []);
};

const normalizeRows = (rows: unknown): DataLockAttributeFormRow[] => {
  if (!Array.isArray(rows)) return [];

  return rows.reduce<DataLockAttributeFormRow[]>((normalizedRows, item) => {
    if (!item || typeof item !== "object") return normalizedRows;

    const row = item as Partial<DataLockAttributeFormRow>;
    if (typeof row.key !== "string" || !row.key.trim()) {
      return normalizedRows;
    }

    const values = Array.isArray(row.values)
      ? row.values.filter((value): value is string => typeof value === "string")
      : [];
    const selectedOptions = Array.isArray(row.selectedOptions)
      ? row.selectedOptions.reduce<DataLockFormOption[]>((options, option) => {
          if (
            option &&
            typeof option === "object" &&
            typeof option.value === "string" &&
            typeof option.label === "string"
          ) {
            options.push({ value: option.value, label: option.label });
          }
          return options;
        }, [])
      : [];

    normalizedRows.push({
      key: row.key,
      field:
        typeof row.field === "string" && row.field ? row.field : undefined,
      allValues:
        typeof row.allValues === "boolean" ? row.allValues : true,
      values,
      selectedOptions,
    });
    return normalizedRows;
  }, []);
};

const cloneRow = (row: DataLockAttributeFormRow): DataLockAttributeFormRow => ({
  ...row,
  values: Array.isArray(row.values) ? [...row.values] : [],
  selectedOptions: Array.isArray(row.selectedOptions)
    ? row.selectedOptions.map((option) => ({ ...option }))
    : [],
});

const cloneRows = (rows: DataLockAttributeFormRow[]) => rows.map(cloneRow);

const createRowKey = (usedKeys: Set<string>) => {
  let key: string;
  do {
    generatedRowKeySequence += 1;
    key = `order-scope-row-${generatedRowKeySequence}`;
  } while (usedKeys.has(key));

  return key;
};

export const OrderScopeEditor = ({
  fields,
  allOption,
  restrictedAttributes,
  whitelistedAttributes,
  disabled = false,
  onChange,
}: OrderScopeEditorProps) => {
  const restrictedRows = normalizeRows(restrictedAttributes);
  const whitelistedRows = normalizeRows(whitelistedAttributes);
  const fieldOptions = normalizeFields(fields);

  const rowsByScope: OrderScopeEditorValue = {
    restrictedAttributes: restrictedRows,
    whitelistedAttributes: whitelistedRows,
  };

  const emitScopeChange = (
    scope: ScopeKey,
    nextRows: DataLockAttributeFormRow[],
  ) => {
    onChange({
      restrictedAttributes: cloneRows(
        scope === "restrictedAttributes" ? nextRows : restrictedRows,
      ),
      whitelistedAttributes: cloneRows(
        scope === "whitelistedAttributes" ? nextRows : whitelistedRows,
      ),
    });
  };

  const addRow = (scope: ScopeKey) => {
    const usedKeys = new Set(
      [...restrictedRows, ...whitelistedRows].map((item) => item.key),
    );
    const nextRow: DataLockAttributeFormRow = {
      key: createRowKey(usedKeys),
      field: undefined,
      allValues: true,
      values: [],
      selectedOptions: [],
    };

    emitScopeChange(scope, [...rowsByScope[scope], nextRow]);
  };

  const updateRow = (
    scope: ScopeKey,
    rowKey: string,
    update: (
      current: DataLockAttributeFormRow,
    ) => DataLockAttributeFormRow,
  ) => {
    emitScopeChange(
      scope,
      rowsByScope[scope].map((item) =>
        item.key === rowKey ? update(item) : item,
      ),
    );
  };

  const renderBlock = (scope: ScopeKey, title: string) => {
    const rows = rowsByScope[scope];

    return (
      <Card
        className={styles.ScopeCard}
        title={title}
        extra={
          <Button
            type="dashed"
            disabled={disabled}
            onClick={() => addRow(scope)}
          >
            Thêm thuộc tính
          </Button>
        }
      >
        <Space
          direction="vertical"
          size="middle"
          className={styles.AttributeList}
        >
          {rows.map((item, rowIndex) => {
            const fieldCode =
              typeof item.field === "string" && item.field
                ? item.field
                : undefined;
            const selectedSiblingFields = new Set(
              rows
                .filter((_, siblingIndex) => siblingIndex !== rowIndex)
                .map((sibling) => sibling.field)
                .filter(
                  (field): field is string =>
                    typeof field === "string" && Boolean(field),
                ),
            );
            const availableFieldOptions = fieldOptions.map((option) => ({
              ...option,
              disabled:
                option.value !== fieldCode &&
                selectedSiblingFields.has(option.value),
            }));

            return (
              <Space
                key={item.key}
                data-row-key={item.key}
                className={styles.AttributeRow}
              >
                <Select<string>
                  className={styles.Control}
                  aria-label="Thuộc tính"
                  value={fieldCode}
                  options={availableFieldOptions}
                  placeholder="Chọn thuộc tính"
                  allowClear
                  disabled={disabled}
                  onChange={(nextField) =>
                    updateRow(scope, item.key, (current) => ({
                      ...current,
                      field:
                        typeof nextField === "string" && nextField
                          ? nextField
                          : undefined,
                      allValues: true,
                      values: [],
                      selectedOptions: [],
                    }))
                  }
                />

                {fieldCode ? (
                  <OrderFieldValueSelect
                    fieldCode={fieldCode}
                    allOption={allOption}
                    allValues={item.allValues}
                    values={item.values}
                    selectedOptions={item.selectedOptions}
                    disabled={disabled}
                    placeholder="Chọn giá trị"
                    onChange={(nextValue) =>
                      updateRow(scope, item.key, (current) => ({
                        ...current,
                        allValues: nextValue.allValues,
                        values: [...nextValue.values],
                        selectedOptions: nextValue.selectedOptions.map(
                          (option) => ({ ...option }),
                        ),
                      }))
                    }
                  />
                ) : (
                  <Select
                    className={styles.Control}
                    aria-label="Giá trị"
                    options={[]}
                    disabled
                    placeholder="Chọn thuộc tính trước"
                  />
                )}

                <Button
                  danger
                  aria-label="Xóa thuộc tính"
                  disabled={disabled}
                  onClick={() =>
                    emitScopeChange(
                      scope,
                      rows.filter((row) => row.key !== item.key),
                    )
                  }
                >
                  Xóa
                </Button>
              </Space>
            );
          })}
        </Space>
      </Card>
    );
  };

  return (
    <Row className={styles.OrderScopeGrid}>
      <Col xs={24} lg={12}>
        {renderBlock("restrictedAttributes", "Thuộc tính khóa")}
      </Col>
      <Col xs={24} lg={12}>
        {renderBlock("whitelistedAttributes", "Thuộc tính loại trừ")}
      </Col>
    </Row>
  );
};

export default OrderScopeEditor;
